import {
  createSeewoIMAuthHeaders,
  createSeewoIMConnectionOptions,
  createSeewoIMInitOptionsLogData,
  DEFAULT_SEEWO_IM_HEARTBEAT,
  DEFAULT_SEEWO_IM_PLATFORM,
  DEFAULT_SEEWO_IM_TIMEOUT,
  DefaultSocket,
  getSeewoIMAuthResponseToken,
  normalizeSeewoIMRuntimeConfig,
  pickFirstDefined,
  registerSeewoIMEventLoggers,
  SocketEventName
} from 'seewo-im-lib';
import type {
  SeewoIMEnvironment,
  SeewoIMRuntimeConfig,
  SeewoSocketInitOptions,
  SeewoSocketOptions
} from 'seewo-im-lib';
import { getIsPriv } from 'config';
import { getCompanyId, getOrgCode, getStaffId, getUserId, getUserToken } from 'data/storage';
import { getRuntimeEnv } from 'util/env';

interface SeewoIMResolvedConfig {
  enabled: boolean;
  env: SeewoIMEnvironment;
  host: string;
  appid: string;
  platform: number;
  debug: boolean;
}

interface SeewoIMAuthData {
  uid?: string;
  imToken?: string;
}

interface SeewoIMAuthResponse {
  code?: number | string;
  success?: boolean;
  succeess?: boolean;
  message?: string;
  data?: SeewoIMAuthData | null;
}

type SingleCommandPayload = {
  msg?: unknown;
  data?: {
    msg?: unknown;
  };
  ctx?: {
    data?: {
      msg?: unknown;
    };
  };
};

declare global {
  interface Window {
    __SEEWOO_IM_CONFIG__?: SeewoIMRuntimeConfig;
  }
}

const IM_CONFIG_API = '/bff/api/im-config';
const SOCKET_AUTH_API = '/bff/api/im-socket-auth';
const MEETING_SCHEDULE_CHANGED_COMMAND = 'meeting_schedule_changed';
const RECONNECT_SCHEDULE_REFRESH_INTERVAL = 3 * 60 * 1000;

export const SEEWO_IM_MEETING_SCHEDULE_CHANGED_EVENT = 'seewo-im:meeting-schedule-changed';

let serverConfig: SeewoIMRuntimeConfig = {};
let serverConfigPromise: Promise<SeewoIMRuntimeConfig> | null = null;
let connectVersion = 0;
let statusLoggerRegistered = false;
let pageConnectionActive = false;
let registeredImToken = '';
let registeredImTokenVersion = 0;
let shouldRefreshScheduleAfterDisconnected = false;
let lastReconnectScheduleRefreshAt = 0;

const logSeewoIM = (stage: string, data?: Record<string, unknown>) => {
  console.log(`[seewo-im] ${stage}`, data || '');
};

const warnSeewoIM = (stage: string, data?: Record<string, unknown>) => {
  console.warn(`[seewo-im] ${stage}`, data || '');
};

const errorSeewoIM = (stage: string, data?: Record<string, unknown>) => {
  console.error(`[seewo-im] ${stage}`, data || '');
};

const getSingleCommandEvent = (data?: unknown, ctx?: unknown) => {
  const payload = data as SingleCommandPayload;
  const context = ctx as SingleCommandPayload;
  const msg = payload?.msg || payload?.data?.msg || payload?.ctx?.data?.msg || context?.msg || context?.data?.msg;
  if (typeof msg !== 'string') return '';

  try {
    const command = JSON.parse(msg);
    return typeof command?.event === 'string' ? command.event : '';
  } catch {
    return '';
  }
};

const emitMeetingScheduleChangedEvent = () => {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(new Event(SEEWO_IM_MEETING_SCHEDULE_CHANGED_EVENT));
};

const handleSingleCommandRecv = (data?: unknown, ctx?: unknown) => {
  if (getSingleCommandEvent(data, ctx) !== MEETING_SCHEDULE_CHANGED_COMMAND) return;

  emitMeetingScheduleChangedEvent();
};

const handleSeewoIMDisconnected = () => {
  if (!pageConnectionActive) return;
  shouldRefreshScheduleAfterDisconnected = true;
};

const handleSeewoIMConnected = () => {
  if (!pageConnectionActive) return;
  if (!shouldRefreshScheduleAfterDisconnected) return;
  shouldRefreshScheduleAfterDisconnected = false;

  const now = Date.now();
  if (now - lastReconnectScheduleRefreshAt < RECONNECT_SCHEDULE_REFRESH_INTERVAL) return;

  lastReconnectScheduleRefreshAt = now;
  emitMeetingScheduleChangedEvent();
};

const readRuntimeConfig = (): SeewoIMRuntimeConfig => {
  return typeof window === 'undefined' ? {} : normalizeSeewoIMRuntimeConfig(window.__SEEWOO_IM_CONFIG__ || {});
};

const readBuildConfig = (): SeewoIMRuntimeConfig =>
  normalizeSeewoIMRuntimeConfig({
    enabled: process.env.REACT_APP_SEEWO_IM_ENABLED,
    env: process.env.REACT_APP_SEEWO_IM_ENV,
    host: process.env.REACT_APP_SEEWO_IM_HOST,
    appid: process.env.REACT_APP_SEEWO_IM_APPID,
    platform: process.env.REACT_APP_SEEWO_IM_PLATFORM,
    debug: process.env.REACT_APP_SEEWO_IM_DEBUG
  });

const getDefaultEnvironment = (): SeewoIMEnvironment => {
  if (getIsPriv()) return 'PRIV';
  const runtimeEnv = getRuntimeEnv();
  if (runtimeEnv === 'PRO') return 'PRO';
  if (runtimeEnv === 'TEST') return 'TEST';
  return 'DEV';
};

const getConnectionSnapshot = () => {
  const config = getSeewoIMConfig();

  return {
    env: config.env,
    host: config.host,
    appid: config.appid,
    enabled: config.enabled,
    platform: config.platform,
    debug: config.debug,
    active: pageConnectionActive,
    hasUid: Boolean(getUserId()),
    hasUserToken: Boolean(getUserToken()),
    hasImToken: Boolean(registeredImToken),
    isConnected: seewoIM.isConnected
  };
};

const getConnectionBlockReasons = (): string[] => {
  const config = getSeewoIMConfig();
  const reasons: string[] = [];

  if (!config.enabled) reasons.push('disabled');
  if (!pageConnectionActive) reasons.push('inactive page');
  if (!config.host) reasons.push('missing host');
  if (!config.appid) reasons.push('missing appid');
  if (!getUserId()) reasons.push('missing uid');
  if (!getUserToken()) reasons.push('missing token');

  return reasons;
};

export const loadSeewoIMConfig = async (): Promise<SeewoIMRuntimeConfig> => {
  if (serverConfigPromise) {
    logSeewoIM('config:reuse-pending-request');
    return serverConfigPromise;
  }
  if (typeof window === 'undefined' || typeof fetch === 'undefined') {
    warnSeewoIM('config:skip-load', { reason: 'window or fetch is unavailable' });
    return serverConfig;
  }

  logSeewoIM('config:load-start', { url: IM_CONFIG_API });

  serverConfigPromise = fetch(IM_CONFIG_API, { credentials: 'same-origin' })
    .then(async (response) => {
      logSeewoIM('config:response', { status: response.status, ok: response.ok });

      if (!response.ok) {
        throw new Error(`load im config failed: ${response.status}`);
      }

      return response.json();
    })
    .then((body: { data?: SeewoIMRuntimeConfig }) => {
      serverConfig = normalizeSeewoIMRuntimeConfig(body.data || {});
      logSeewoIM('config:load-success', getConnectionSnapshot());
      return serverConfig;
    })
    .catch((error) => {
      serverConfigPromise = null;
      warnSeewoIM('config:load-failed', { error });
      return serverConfig;
    });

  return serverConfigPromise;
};

export const getSeewoIMConfig = (): SeewoIMResolvedConfig => {
  const runtimeConfig = readRuntimeConfig();
  const buildConfig = readBuildConfig();
  const env = pickFirstDefined(runtimeConfig.env, serverConfig.env, buildConfig.env, getDefaultEnvironment()) as SeewoIMEnvironment;
  const host = pickFirstDefined(runtimeConfig.host, serverConfig.host, buildConfig.host, '') || '';
  const appid = pickFirstDefined(runtimeConfig.appid, serverConfig.appid, buildConfig.appid, '') || '';
  const enabled = pickFirstDefined(runtimeConfig.enabled, serverConfig.enabled, buildConfig.enabled);

  return {
    enabled: Boolean((enabled === undefined ? true : enabled) && host && appid),
    env,
    host,
    appid,
    platform: pickFirstDefined(runtimeConfig.platform, serverConfig.platform, buildConfig.platform, DEFAULT_SEEWO_IM_PLATFORM) || DEFAULT_SEEWO_IM_PLATFORM,
    debug: Boolean(pickFirstDefined(runtimeConfig.debug, serverConfig.debug, buildConfig.debug, env !== 'PRO'))
  };
};

const hasConnectionContext = () => {
  const config = getSeewoIMConfig();
  return Boolean(pageConnectionActive && config.enabled && config.host && config.appid && getUserId() && getUserToken());
};

const getRegisteredImToken = (version?: number) => {
  if (version !== undefined && registeredImTokenVersion !== version) return '';
  return registeredImToken;
};

const registerSeewoIMStatusLogger = () => {
  if (statusLoggerRegistered) return;
  statusLoggerRegistered = true;

  registerSeewoIMEventLoggers(seewoIM, {
    getSnapshot: getConnectionSnapshot,
    log: logSeewoIM,
    warn: warnSeewoIM,
    error: errorSeewoIM
  });
  seewoIM.addSingleCommandRecv(handleSingleCommandRecv);
  seewoIM.addEventListener(SocketEventName.onDisconnected, handleSeewoIMDisconnected);
  seewoIM.addEventListener(SocketEventName.onConnected, handleSeewoIMConnected);
};

const createAuthHeaders = (): Record<string, string> => {
  return createSeewoIMAuthHeaders({
    userToken: getUserToken(),
    userId: getUserId(),
    staffId: getStaffId(),
    companyId: getCompanyId(),
    orgCode: getOrgCode()
  });
};

const isStaleConnectVersion = (version?: number) => {
  return version !== undefined && version !== connectVersion;
};

const parseAuthResponseBody = async (response: Response): Promise<SeewoIMAuthResponse | null> => {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch (error) {
    return { message: 'non-json response' };
  }
};

const isAuthResponseSuccess = (body: SeewoIMAuthResponse | null) => {
  if (!body) return true;
  if (body.success === false || body.succeess === false) return false;
  if (body.code === undefined || body.code === null || body.code === '') return true;

  const code = Number(body.code);
  return code === 0 || code === 200;
};

const getAuthResponseImToken = (body: SeewoIMAuthResponse | null) => {
  return getSeewoIMAuthResponseToken(body);
};

const authSeewoSocket = async (version?: number) => {
  if (isStaleConnectVersion(version) || !pageConnectionActive) {
    warnSeewoIM('auth:cancelled', { version, currentVersion: connectVersion, reason: 'stale request' });
    return false;
  }

  if (registeredImToken && registeredImTokenVersion === version) {
    logSeewoIM('auth:reuse-token', { version, hasImToken: true });
    return true;
  }

  registeredImToken = '';
  registeredImTokenVersion = 0;
  const headers = createAuthHeaders();

  logSeewoIM('auth:load-start', { url: SOCKET_AUTH_API });
  logSeewoIM('auth:request', {
    url: SOCKET_AUTH_API,
    method: 'POST',
    hasAuthorization: Boolean(headers.Authorization),
    hasUserId: Boolean(headers['x-auth-userId']),
    hasStaffId: Boolean(headers['x-staff-id']),
    hasCompanyId: Boolean(headers['x-company-id'])
  });
  const response = await fetch(SOCKET_AUTH_API, {
    method: 'POST',
    credentials: 'same-origin',
    headers
  });
  const body = await parseAuthResponseBody(response);
  const imToken = getAuthResponseImToken(body);

  logSeewoIM('auth:response', {
    status: response.status,
    ok: response.ok,
    code: body?.code,
    message: body?.message,
    uid: body?.data?.uid,
    hasImToken: Boolean(imToken)
  });

  if (!response.ok || !isAuthResponseSuccess(body)) {
    throw new Error(`auth im socket failed: status=${response.status}, code=${body?.code || ''}, message=${body?.message || ''}`);
  }

  if (isStaleConnectVersion(version) || !pageConnectionActive) {
    warnSeewoIM('auth:cancelled', { version, currentVersion: connectVersion, reason: 'stale request' });
    return false;
  }

  if (!imToken) {
    throw new Error(`auth im socket failed: missing imToken, status=${response.status}, code=${body?.code || ''}, message=${body?.message || ''}`);
  }

  registeredImToken = imToken;
  registeredImTokenVersion = version || 0;
  logSeewoIM('auth:success', { version, status: response.status, uid: body?.data?.uid, hasImToken: true });

  return true;
};

const createInitOptionsLogData = (version: number | undefined, initOptions: SeewoSocketInitOptions) => {
  return createSeewoIMInitOptionsLogData(version, initOptions);
};

const createSeewoIMOptions = (version?: number): SeewoSocketOptions =>
  createSeewoIMConnectionOptions({
    host: () => getSeewoIMConfig().host,
    appid: () => getSeewoIMConfig().appid,
    uid: getUserId,
    token: () => getRegisteredImToken(version),
    platform: () => getSeewoIMConfig().platform,
    timeout: DEFAULT_SEEWO_IM_TIMEOUT,
    heartbeat: DEFAULT_SEEWO_IM_HEARTBEAT,
    debug: () => getSeewoIMConfig().debug,
    isSupport: hasConnectionContext,
    beforeConnect: () => authSeewoSocket(version),
    onBeforeInit: (initOptions) => logSeewoIM('init:options', createInitOptionsLogData(version, initOptions)),
    logger: {
      log: (...args: unknown[]) => logSeewoIM('sdk:log', { args }),
      warn: (...args: unknown[]) => warnSeewoIM('sdk:warn', { args }),
      error: (...args: unknown[]) => errorSeewoIM('sdk:error', { args })
    }
  });

export const seewoIM = DefaultSocket.getInstance(createSeewoIMOptions());

export const getSeewoIM = (version?: number) => {
  seewoIM.configure(createSeewoIMOptions(version));
  return seewoIM;
};

export const connectSeewoIM = async () => {
  const version = ++connectVersion;
  pageConnectionActive = true;
  logSeewoIM('connect:requested', { version, ...getConnectionSnapshot() });
  await loadSeewoIMConfig();
  const im = getSeewoIM(version);
  const snapshot = getConnectionSnapshot();

  logSeewoIM('connect:config-ready', { version, ...snapshot });

  if (version !== connectVersion || !pageConnectionActive) {
    warnSeewoIM('connect:cancelled', { version, currentVersion: connectVersion, reason: 'stale request' });
    return im;
  }

  if (im.isConnected) {
    logSeewoIM('connect:skip', { version, reason: 'already connected', ...snapshot });
    return im;
  }

  const blockReasons = getConnectionBlockReasons();
  if (blockReasons.length > 0) {
    warnSeewoIM('connect:skip', { version, reasons: blockReasons, ...snapshot });
    return im;
  }

  try {
    const authReady = await authSeewoSocket(version);
    if (!authReady) {
      warnSeewoIM('connect:skip', { version, reason: 'auth failed', ...getConnectionSnapshot() });
      return im;
    }
  } catch (error) {
    errorSeewoIM('connect:auth-error', { version, error });
    return im;
  }

  registerSeewoIMStatusLogger();
  logSeewoIM('connect:start', { version, ...getConnectionSnapshot() });
  await im.connect();
  if (version !== connectVersion || !pageConnectionActive) {
    warnSeewoIM('connect:cancelled', { version, currentVersion: connectVersion, reason: 'stale request' });
    return im;
  }
  logSeewoIM('connect:called-sdk', { version, transport: 'IM', isConnected: im.isConnected });
  return im;
};

export const disconnectSeewoIM = () => {
  connectVersion += 1;
  pageConnectionActive = false;
  registeredImToken = '';
  registeredImTokenVersion = 0;
  shouldRefreshScheduleAfterDisconnected = false;
  const snapshot = getConnectionSnapshot();
  logSeewoIM('disconnect:requested', { nextVersion: connectVersion, ...snapshot });
  getSeewoIM().close();
  statusLoggerRegistered = false;
  logSeewoIM('disconnect:closed', { nextVersion: connectVersion, isConnected: seewoIM.isConnected });
};

export { SocketEventName };
export type { SeewoSocketOptions };
